import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Sliderr from 'react-slick';

const Slider: typeof Sliderr = (Sliderr as unknown as { default: typeof Sliderr }).default;

import { trackSoftConversionEvent } from '../../actions/tracking';
import ControlBar from '../../components/gallery/control-bar';
import DefaultGalleryFactory from '../../components/gallery/gallery-factory';
import ModalVideoPlayer from '../../components/gallery/modal-video-player';
import { cdn } from '../../helpers/urls';
import { getItem } from '../../selectors/item';
import { getControlMenu, getFirstMedia, getMedia } from '../../selectors/media';
import { softConversionEvents } from '../../shared/tracking';
import ShareButton from '../../styled-components/structures/ShareButton';
import LightboxShell from './lightbox-shell';
import Save from './save';
import SaveModal from './save-modal';

const Placeholder = () => {
  const { t } = useTranslation('item');

  return (
    <div className="slick-slide-item is-current">
      <div className="slick-slide-image-container">
        <img
          src={cdn('/images/placeholders/placeholder_product_card.jpg')}
          alt={t('Image missing', { ns: 'item' })}
          className="slick-slide-image"
        />
      </div>
    </div>
  );
};

interface GalleryArrowType {
  onClick?: () => void;
  currentSlide?: number;
  slideCount?: number;
}

const PrevArrow = (props: GalleryArrowType | undefined) => {
  const { t } = useTranslation('item');

  if (!props) return null;
  const arrowProps = { ...props };
  delete arrowProps.currentSlide;
  delete arrowProps.slideCount;
  return (
    <button
      {...arrowProps}
      type="button"
      title={t('Previous image', { ns: 'item' })}
      className="slick-arrow slick-arrow-prev"
    >
      <i className="icon-chevron-left no-margin" />
    </button>
  );
};
const NextArrow = (props: GalleryArrowType | undefined) => {
  const { t } = useTranslation('item');

  if (!props) return null;
  const arrowProps = { ...props };
  delete arrowProps.currentSlide;
  delete arrowProps.slideCount;
  return (
    <button
      {...arrowProps}
      type="button"
      title={t('Next image', { ns: 'item' })}
      className="slick-arrow slick-arrow-next"
    >
      <i className="icon-chevron-right no-margin" />
    </button>
  );
};

let block = false;
const Gallery = () => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const media = useSelector(getMedia);
  const item = useSelector(getItem);
  const firstMedia = useSelector(getFirstMedia);
  const controlMenu = useSelector(getControlMenu);
  const track = bindActionCreators(
    trackSoftConversionEvent,

    dispatch,
  );

  const galleryRef = useRef<HTMLDivElement | null>(null);
  const [modalSave, setModalSave] = useState('');
  const sliderRef = useRef<any>(null);
  const [hasInitializedDots, setHasInitializedDots] = useState(false);
  const [state, setState] = useState({
    index: 0,
    lightbox: false,
    proportions: 0,
    displayMediaInModal: null as string | null,
    navigationDisabled: false,
    isDragging: false,
    scrollIntoIndex: 0,
    init: false,
  });

  const dotHandler = (index: number) => {
    if (galleryRef.current) {
      const container = galleryRef.current.querySelector('.slick-dots') as HTMLStyleElement;

      if (container && container.children.length > 7) {
        container.style.transform = `translateX(${(15 + 16) * -index}px)`;
        container.style.transition = '300ms ease';
        for (let i = 0; i < container.children.length; i++) {
          const dot = container.children[i] as HTMLStyleElement;

          if (i === index) {
            dot.className = 'slick-active';
            dot.style.transform = `translateX(${15 * index}px)`;
          } else if (i === index - 1 || i === index + 1) {
            dot.className = '';
            dot.style.transform = `translateX(${15 * index}px)`;
          } else if (i === index - 2 || i === index + 2) {
            dot.className = 'is-medium';
            dot.style.transform = `translateX(${15 * index}px) scale(0.75)`;
          } else if (i === index - 3 || i === index + 3) {
            dot.className = 'is-small';
            dot.style.transform = `translateX(${15 * index}px) scale(0.5)`;
          } else {
            dot.className = 'is-hidden';
            dot.style.transform = `translateX(${15 * index}px) scale(0.00001)`;
          }
        }
      } else if (!!container) {
        container.style.transform = 'translateX(-50%)';
      }

      setHasInitializedDots(true);
    }
  };

  useEffect(() => {
    if (!state.navigationDisabled) dotHandler(state.index);
  }, [state.navigationDisabled]);

  useEffect(() => {
    block = false;
    if (firstMedia) {
      const image = new Image();
      image.src = firstMedia;
      image.onload = () => {
        if (!block) setState((s) => ({ ...s, proportions: (image.height / image.width) * 100 }));
      };
    }

    return () => {
      block = true;
    };
  }, [firstMedia]);

  const onTrack = () => track(item, softConversionEvents.SOFT_CONVERSION_SHOW_IMAGES);

  const onToggleLightboxGallery = (index: number) => {
    if (!state.isDragging) {
      setState({ ...state, lightbox: !state.lightbox, scrollIntoIndex: index });
    }
  };

  const onFullscreen = () => {
    track(item, softConversionEvents.SOFT_CONVERSION_FULLSCREEN_IMAGES);
    setState({ ...state, lightbox: true, scrollIntoIndex: 0 });
  };
  const onCloseFullscreen = () => setState({ ...state, lightbox: false });

  const onBeforeChange = (_: number, index: number) => {
    onTrack();
    setState({ ...state, index });
    dotHandler(index);
  };

  const onSwipeToIndex = (index: number) => {
    if (!sliderRef || !sliderRef.current) return;
    sliderRef.current.slickGoTo(index);
  };
  const onMouseDown = () => {
    if (state.isDragging) setState({ ...state, isDragging: false });
  };
  const onMouseMove = () => {
    if (!state.isDragging) setState({ ...state, isDragging: true });
  };

  const onDisplayMediaInModal = (displayMediaInModal: string) => {
    if (!state.isDragging) setState({ ...state, displayMediaInModal });
  };
  const onCloseMediaInModal = (): void => setState({ ...state, displayMediaInModal: null });
  const onDisableNavigation = () => {
    if (!state.isDragging) setState({ ...state, navigationDisabled: true });
  };
  const onEnableNavigation = () => {
    setState({ ...state, navigationDisabled: false, isDragging: false });
  };

  const additional = {
    proportions: state.proportions,
    onDisableNavigation,
    navigationDisabled: state.navigationDisabled,
    imageAlt: '',
    onFullscreen: onToggleLightboxGallery,
    onDisplayMediaInModal,
  };

  const imgs = (media || []).map((m: any, index: number) =>
    DefaultGalleryFactory.parse(m, { ...additional, index, visible: index === state.index }),
  );

  const infinite = imgs.length < 2;
  const navigationDisabled = infinite ? false : !state.navigationDisabled;

  const settings = {
    swipe: navigationDisabled,
    touchMove: navigationDisabled,
    swipeToSlide: navigationDisabled,
    speed: 300,
    dots: navigationDisabled,
    arrows: navigationDisabled,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    beforeChange: onBeforeChange,
    infinite: navigationDisabled,
  };

  let button: any = null;
  if (imgs && imgs.length > 0 && state.navigationDisabled) {
    button = (
      <button
        type="button"
        className="btn-exit-state"
        onClick={onEnableNavigation}
        title={t('closeModal', { ns: 'common' })}
      >
        {t('close', { ns: 'common' })}
      </button>
    );
  }

  let countImg = 0;
  let countSphere = 0;
  let countVideo = 0;

  media.map((m) => {
    if (m.type === 'Image' && m.files.length < 2) {
      countImg += 1;
    } else if (m.type === 'Image' && m.files.length > 1) {
      countSphere += 1;
    } else if (m.type === 'Sphere') {
      countSphere += 1;
    } else if (m.type === 'Embedded') {
      countVideo += 1;
    }
  });

  return (
    <>
      <div className="item-section item-section-gallery" ref={galleryRef}>
        {!hasInitializedDots && (
          <style
            dangerouslySetInnerHTML={{
              __html: `
                        [data-am-slick] .slick-dots > li {
                            display: none !important;
                        }
                    `,
            }}
          />
        )}
        {state.displayMediaInModal && (
          <ModalVideoPlayer url={state.displayMediaInModal} onClose={onCloseMediaInModal} />
        )}
        {state.lightbox && (
          <LightboxShell onClose={onCloseFullscreen} startIndex={state.scrollIntoIndex}>
            {imgs.length ? (
              imgs.map((x: any, index: number) => (
                <div key={index} className="lightbox-item" id={`lightbox-item-${index}`}>
                  {x.fullscreen()}
                </div>
              ))
            ) : (
              <Placeholder />
            )}
          </LightboxShell>
        )}
        <div className="item-content-gallery">
          <div className="item-content-gallery-window-container">
            <div className="item-content-gallery-window">
              <div
                data-am-slick="overflow-visible"
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
              >
                <Slider ref={sliderRef} {...settings}>
                  {imgs.length ? imgs.map((x: any) => x.default()) : <Placeholder />}
                </Slider>
              </div>
              {button}
            </div>
          </div>
        </div>
      </div>
      <div className="item-section item-section-gallery-controls">
        <div data-wx-userutility="">
          <div className="user-utility-gallery">
            <ControlBar
              controlMenu={controlMenu}
              onSwipeToIndex={onSwipeToIndex}
              onFullscreen={onFullscreen}
              countImg={countImg}
              countSphere={countSphere}
              countVideo={countVideo}
            />
          </div>
          <div className="user-utility-social">
            <ul className="user-utility-list">
              <li className="user-utility-item">
                <ShareButton />
              </li>
              <li className="user-utility-item">
                <Save onClick={setModalSave} />
              </li>
            </ul>
          </div>
        </div>
      </div>
      {modalSave && <SaveModal onClose={() => setModalSave('')} modalType={modalSave} />}
    </>
  );
};

export default Gallery;
